import * as React from "react"
import { cva, type VariantProps } from "cva"
import { clx } from "../utils/clx"
import { MagnifyingGlassMiniIcon } from "~/design-system"

const inputBaseStyles = clx(
  "transition-fg relative w-full bg-grey-0 border border-ui-border-strong hover:border-ui-border-hover shadow-borders-with-shadow text-ui-fg-base appearance-none rounded-md outline-none",
  "focus-visible:shadow-borders-interactive-with-active",
  "disabled:bg-ui-bg-disabled disabled:border-ui-border-subtle disabled:text-ui-fg-muted disabled:placeholder-ui-fg-muted disabled:shadow-none disabled:cursor-not-allowed",
  "aria-[invalid=true]:!shadow-borders-error invalid:!shadow-borders-error",
)

const inputVariants = cva({
  base: clx(
    inputBaseStyles,
    "[&::--webkit-search-cancel-button]:hidden [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
  ),
  variants: {
    size: {
      base: "txt-base-regular h-10 px-3 py-2",
      small: "txt-base-regular h-9 px-2 py-1.5",
    },
  },
  defaultVariants: {
    size: "base",
  },
})


interface InputProps
  extends VariantProps<typeof inputVariants>,
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {}

/**
 * This component is based on the `input` element and supports all of its props
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      /**
       * The input's size.
       */
      size = "base",
      ...props
    }: InputProps,
    ref
  ) => {
    const isSearch = type === "search"

    return (
      <div className="relative">
        <input
          ref={ref}
          type={type}
          className={clx(
            inputVariants({ size: size }),
            {
              "pl-9": isSearch && size === "base",
              "pl-8": isSearch && size === "small",
            },
            className
          )}
          {...props}
        />
        {isSearch && (
          <div
            className={clx(
              "text-ui-fg-muted pointer-events-none absolute bottom-0 left-0 flex items-center justify-center",
              {
                "h-10 w-10": size === "base",
                "h-9 w-9": size === "small",
              }
            )}
            role="img"
          >
            <MagnifyingGlassMiniIcon />
          </div>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input, inputBaseStyles }
